import React from 'react'
import moment from 'moment'
import find from 'lodash/find'
import pick from 'lodash/pick'
import omit from 'lodash/omit'
import startCase from 'lodash/startCase'
import { NavLink } from 'react-router-dom'

import countries from './countries.json'
import states from './states.json'
import cities from './cities.json'

const contactFields = [
  'email',
  'phone',
  'userName',
  'clientCode',
  'strategyCode',
]
const staffUserFields = [
  'firstName',
  'lastName',
  'email',
  'phone',
  'dateOfBirth',
  'userName',
  'otherMailIds',
]
const userFields = [
  'firstName',
  'lastName',
  'email',
  'phone',
  'address',
  'cityId',
  'otherMailIds',
  'stateId',
  'countryId',
  'motherName',
  'fatherName',
  'dateOfBirth',
  'userName',
  'keyId',
  'fundReceived',
  'amcStartDate',
  'sipStartDate',
  'closedDate',
  'initialExposure',
  'clientCode',
  'strategyCode',
  'amcCycle',
]

export function getName(data) {
  if (!data) return '-'
  const { firstName, lastName, name } = data
  if (name) return name
  if (!firstName && !lastName) return '-'
  if (firstName && lastName) return `${firstName} ${lastName}`
  if (lastName) return lastName
  return firstName
}

export function getFirstName(data) {
  if (!data) return '-'
  const { firstName } = data
  if (!firstName) return '-'
  return firstName
}
export function getLastName(data) {
  if (!data) return '-'
  const { lastName } = data
  if (!lastName) return '-'
  return lastName
}

export function getEmail(data) {
  const { email } = data
  if (!email) return ''
  return email
}

export function getUserNavLinkByObject(data, isClient) {
  if (!data) return ''
  if (isClient) return getName(data)
  const nav = !!data.role && data.role === 'CLIENT' ? 'clients' : 'users'
  return <NavLink to={`/${nav}/${data.id}`}>{getName(data)}</NavLink>
}

export function getFirstLetter(data) {
  const name = getName(data)
  return name.charAt(0).toUpperCase()
}

export function getCityState(data) {
  const { cityId, stateId } = data
  if (!cityId && !stateId) return '-'
  if (cityId && !stateId && find(cities, { id: cityId }))
    return find(cities, { id: cityId }).name
  if (!cityId && stateId && find(states, { id: stateId }))
    return find(states, { id: stateId }).name
  if (
    cityId &&
    stateId &&
    find(states, { id: stateId }) &&
    find(cities, { id: cityId })
  )
    return `${find(cities, { id: cityId }).name}, ${find(states, { id: stateId }).name
      }`
  return '-'
}

export function userDocuments(kyc, isClient, onProtectedView) {
  const documents = omit(kyc, 'id', '__typename')
  return Object.keys(documents).map((key) => {
    const desc = documents[key] ? (
      onProtectedView ? (
        <span
          style={{ cursor: 'pointer', color: '#1890ff', textDecoration: 'underline' }}
          onClick={() => onProtectedView(documents[key], startCase(key))}
        >
          View Document
        </span>
      ) : (
        <span
          style={{ cursor: 'pointer', color: '#1890ff', textDecoration: 'underline' }}
          onClick={() => {
            if (typeof window !== 'undefined') {
              window.dispatchEvent(
                new CustomEvent('OPEN_SECURE_DOC', {
                  detail: {
                    fileUrl: documents[key],
                    title: startCase(key),
                  },
                })
              )
            }
          }}
        >
          View Document
        </span>
      )
    ) : (
      '-'
    )
    const icon =
      documents.length && documents[key]
        ? ['jpeg', 'png', 'jpg'].includes(documents[key]?.split('.')?.pop())
          ? 'image'
          : 'editor'
        : 'editor'
    return { title: startCase(key), icon, desc }
  })
}

export function staffUsersData(userData) {
  return pick(userData, staffUserFields)
}

export function userDetails(details) {
  details = pick(details, userFields)
  return Object.keys(details).map((key) => {
    switch (key) {
      case 'amcStartDate':
      case 'sipStartDate':
      case 'closedDate':
        const closedDate = details[key]
          ? moment(details[key]).format('Do MMMM YYYY')
          : '-'
        return { title: startCase(key), icon: 'datepicker', desc: closedDate }
      case 'fundReceived':
        return {
          title: startCase(key),
          icon: 'revenue-new',
          desc: details[key],
        }
      case 'birthday':
      case 'dateOfBirth':
        const birthday = details[key]
          ? moment(details[key]).format('DD MMMM YYYY')
          : '-'
        return { title: startCase(key), icon: 'birthday-new', desc: birthday }
      case 'cityId':
        let city = find(cities, { id: details[key] })
        city = city ? city.name : ''
        return { title: 'City', icon: 'map-drawing', desc: city }
      case 'stateId':
        let state = find(states, { id: details[key] })
        state = state ? state.name : ''
        return { title: 'State', icon: 'map-drawing', desc: state }
      case 'countryId':
        let country = find(countries, { id: details[key] })
        country = country ? country.name : ''
        return { title: 'Country', icon: 'map-drawing', desc: country }
      case 'address':
        return {
          title: startCase(key),
          icon: 'map-drawing',
          desc: details[key],
        }
      case 'userName':
        return {
          title: startCase(key),
          icon: 'profile2',
          desc: <span className='highlight'>{details[key]}</span>,
        }
      case 'ketId':
      case 'clientCode':
      case 'strategyCode':
        return {
          title: startCase(key),
          icon: 'inputnumber',
          desc: <span className='highlight'>{details[key]}</span>,
        }
      case 'amcCycle': {
        const amcCycleLabel =
          details[key] === 'HALF_YEARLY'
            ? '6Month'
            : details[key] === 'YEARLY'
              ? 'Yearly'
              : details[key]
        return {
          title: startCase(key),
          icon: 'inputnumber',
          desc: <span className='highlight'>{amcCycleLabel}</span>,
        }
      }
      case 'phone':
        return {
          title: startCase(key),
          icon: 'phone',
          desc: details[key] ? (
            <a href={`tel:${details[key]}`}>{details[key]}</a>
          ) : (
            ''
          ),
        }
      case 'email':
        return {
          title: startCase(key),
          icon: 'email',
          desc: details[key] ? (
            <a href={`mailto:${details[key]}`}>{details[key]}</a>
          ) : (
            ''
          ),
        }
      case 'otherMailIds':
        const otherMail = details[key]?.join(', \n')
        return { title: startCase(key), icon: 'birthday-new', desc: otherMail }
      default:
        return {
          title: startCase(key),
          icon: 'auth-screen',
          desc: details[key],
        }
    }
  })
}

export function contactDetails(details) {
  details = pick(details, contactFields)
  return Object.keys(details).map((key) => {
    switch (key) {
      case 'clientCode':
      case 'strategyCode':
        return {
          title: startCase(key),
          icon: 'inputnumber',
          desc: <span className='highlight'>{details[key]}</span>,
        }
      case 'phone':
        return {
          title: startCase(key),
          icon: 'phone',
          desc: details[key] ? (
            <a href={`tel:${details[key]}`}>{details[key]}</a>
          ) : (
            ''
          ),
        }
      case 'email':
        return {
          title: startCase(key),
          icon: 'email',
          desc: details[key] ? (
            <a href={`mailto:${details[key]}`}>{details[key]}</a>
          ) : (
            ''
          ),
        }
      default:
        return {
          title: startCase(key),
          icon: 'auth-screen',
          desc: details[key],
        }
    }
  })
}

export const userRoles = {
  ADMIN: [
    { text: 'ADMIN', value: 'ADMIN' },
    { text: 'STAFF', value: 'STAFF' },
    { text: 'MASTER ASSOCIATE', value: 'MASTER_ASSOCIATE' },
    { text: 'ASSOCIATE', value: 'ASSOCIATE' },
    { text: 'AIF SALES EXECUTIVE', value: 'SALES_EXECUTIVE' },
    { text: 'AUDIT', value: 'AUDIT' },
  ],
  STAFF: [
    { text: 'STAFF', value: 'STAFF' },
    { text: 'MASTER ASSOCIATE', value: 'MASTER_ASSOCIATE' },
    { text: 'ASSOCIATE', value: 'ASSOCIATE' },
    { text: 'AIF SALES EXECUTIVE', value: 'SALES_EXECUTIVE' },
  ],
  MASTER_ASSOCIATE: [{ text: 'ASSOCIATE', value: 'ASSOCIATE' }],
  SALES_EXECUTIVE: [
    { text: 'MASTER ASSOCIATE', value: 'MASTER_ASSOCIATE' },
    { text: 'ASSOCIATE', value: 'ASSOCIATE' },
    { text: 'AIF SALES EXECUTIVE', value: 'SALES_EXECUTIVE' },
  ],
  AUDIT: [
    { text: 'ADMIN', value: 'ADMIN' },
    { text: 'STAFF', value: 'STAFF' },
    { text: 'MASTER ASSOCIATE', value: 'MASTER_ASSOCIATE' },
    { text: 'ASSOCIATE', value: 'ASSOCIATE' },
    { text: 'AIF SALES EXECUTIVE', value: 'SALES_EXECUTIVE' },
    { text: 'AUDIT', value: 'AUDIT' },
  ],
}

export const eMandateStatus = [
  {
    text: 'Mandate',
    value: 'E_MANDATE',
  },
  { text: 'Non Mandate', value: 'NON_E_MANDATE' },
]

export const amcStatus = [
  {
    text: 'Pending',
    value: 'PENDING',
  },
  { text: 'Completed', value: 'COMPLETED' },
]

export const transactionStatus = [
  { text: 'SUCCESS', value: 'SUCCESS' },
  { text: 'FAILED', value: 'FAILED' },
  { text: 'PENDING', value: 'PENDING' },
  { text: 'EXPIRED', value: 'EXPIRED' },
  { text: 'REJECTED', value: 'REJECTED' },
  { text: 'SPAM', value: 'SPAM' },
  { text: 'FAILURE', value: 'FAILURE' },
]

export const amcType = [
  { text: 'New', value: 'New' },
  { text: 'Renewal', value: 'Renewal' },
  { text: 'Existing New', value: 'Existing New' },
]

export const advisorySendTypeFilters = [
  { text: 'Auto', value: 'Auto' },
  { text: 'Admin', value: 'Admin' },
]

export const advisoryTypeFilter = [
  { text: 'New', value: 'New' },
  { text: 'Customize Advisory', value: 'Customize Advisory' },
  { text: 'Bulk Entry', value: 'Bulk Entry' },
  { text: 'Exit', value: 'Exit' },
  { text: 'FundParking', value: 'FundParking' },
  { text: 'Rollover', value: 'Rollover' },
  { text: 'Shifting', value: 'Shifting' },
]

export function getGlobalUserPermissions() {
  if (typeof window !== 'undefined') {
    if (window.__CURRENT_USER_PERMISSIONS__) {
      return window.__CURRENT_USER_PERMISSIONS__
    }
    try {
      const cached = sessionStorage.getItem('finideas_user_permissions')
      if (cached) {
        window.__CURRENT_USER_PERMISSIONS__ = JSON.parse(cached)
        return window.__CURRENT_USER_PERMISSIONS__
      }
    } catch (e) {}
  }
  return null
}

export function canUserView(target, isClient) {
  if (isClient) return true
  if (target && target.role === 'CLIENT') return true

  if (target && target.permissions && typeof target.permissions.canView === 'boolean') {
    return target.permissions.canView
  }
  if (target && target.userPermissions && typeof target.userPermissions.canView === 'boolean') {
    return target.userPermissions.canView
  }
  if (target && typeof target.canView === 'boolean') {
    return target.canView
  }
  const globalPerms = getGlobalUserPermissions()
  if (globalPerms && typeof globalPerms.canView === 'boolean') {
    return globalPerms.canView
  }
  return true
}

export function canUserExport(target, isClient) {
  if (isClient) return true
  if (target && target.role === 'CLIENT') return true

  if (target && target.permissions && typeof target.permissions.canExport === 'boolean') {
    return target.permissions.canExport
  }
  if (target && target.userPermissions && typeof target.userPermissions.canExport === 'boolean') {
    return target.userPermissions.canExport
  }
  if (target && typeof target.canExport === 'boolean') {
    return target.canExport
  }
  const globalPerms = getGlobalUserPermissions()
  if (globalPerms && typeof globalPerms.canExport === 'boolean') {
    return globalPerms.canExport
  }
  if (target && target.role === 'AUDIT') return false
  if (target && ['ADMIN', 'STAFF'].includes(target.role)) return true

  return true
}

export function canUserDownload(target, isClient) {
  if (isClient) return true
  if (target && target.role === 'CLIENT') return true

  if (target && target.permissions && typeof target.permissions.canDownload === 'boolean') {
    return target.permissions.canDownload
  }
  if (target && target.userPermissions && typeof target.userPermissions.canDownload === 'boolean') {
    return target.userPermissions.canDownload
  }
  if (target && typeof target.canDownload === 'boolean') {
    return target.canDownload
  }
  const globalPerms = getGlobalUserPermissions()
  if (globalPerms && typeof globalPerms.canDownload === 'boolean') {
    return globalPerms.canDownload
  }
  if (target && target.role === 'AUDIT') return false
  if (target && ['ADMIN', 'STAFF'].includes(target.role)) return true

  return true
}

export function canUserPrint(target, isClient) {
  if (isClient) return true
  if (target && target.role === 'CLIENT') return true

  if (target && target.permissions && typeof target.permissions.canPrint === 'boolean') {
    return target.permissions.canPrint
  }
  if (target && target.userPermissions && typeof target.userPermissions.canPrint === 'boolean') {
    return target.userPermissions.canPrint
  }
  if (target && typeof target.canPrint === 'boolean') {
    return target.canPrint
  }
  const globalPerms = getGlobalUserPermissions()
  if (globalPerms && typeof globalPerms.canPrint === 'boolean') {
    return globalPerms.canPrint
  }
  if (target && target.role === 'AUDIT') return false
  if (target && ['ADMIN', 'STAFF'].includes(target.role)) return true

  return true
}

export function canUserEdit(target, isClient) {
  if (isClient) return true
  if (target && target.role === 'CLIENT') return true

  if (target && target.permissions && typeof target.permissions.canEdit === 'boolean') {
    return target.permissions.canEdit
  }
  if (target && target.userPermissions && typeof target.userPermissions.canEdit === 'boolean') {
    return target.userPermissions.canEdit
  }
  if (target && typeof target.canEdit === 'boolean') {
    return target.canEdit
  }
  const globalPerms = getGlobalUserPermissions()
  if (globalPerms && typeof globalPerms.canEdit === 'boolean') {
    return globalPerms.canEdit
  }
  if (target && target.role === 'AUDIT') return false
  if (target && ['ADMIN', 'STAFF'].includes(target.role)) return true

  return true
}

export function canUserDelete(target, isClient) {
  if (isClient) return true
  if (target && target.role === 'CLIENT') return true

  if (target && target.permissions && typeof target.permissions.canDelete === 'boolean') {
    return target.permissions.canDelete
  }
  if (target && target.userPermissions && typeof target.userPermissions.canDelete === 'boolean') {
    return target.userPermissions.canDelete
  }
  if (target && typeof target.canDelete === 'boolean') {
    return target.canDelete
  }
  const globalPerms = getGlobalUserPermissions()
  if (globalPerms && typeof globalPerms.canDelete === 'boolean') {
    return globalPerms.canDelete
  }
  if (target && target.role === 'AUDIT') return false
  if (target && ['ADMIN', 'STAFF'].includes(target.role)) return true

  return true
}

export function canUserAdd(target, isClient) {
  if (isClient) return true
  if (target && target.role === 'CLIENT') return true

  if (target && target.permissions && typeof target.permissions.canAdd === 'boolean') {
    return target.permissions.canAdd
  }
  if (target && target.userPermissions && typeof target.userPermissions.canAdd === 'boolean') {
    return target.userPermissions.canAdd
  }
  if (target && typeof target.canAdd === 'boolean') {
    return target.canAdd
  }
  const globalPerms = getGlobalUserPermissions()
  if (globalPerms && typeof globalPerms.canAdd === 'boolean') {
    return globalPerms.canAdd
  }
  if (target && target.role === 'AUDIT') return false
  if (target && ['ADMIN', 'STAFF'].includes(target.role)) return true

  return true
}

export function canUserSave(target, isClient) {
  if (isClient) return true
  if (target && target.role === 'CLIENT') return true

  if (target && target.permissions && typeof target.permissions.canSave === 'boolean') {
    return target.permissions.canSave
  }
  if (target && target.userPermissions && typeof target.userPermissions.canSave === 'boolean') {
    return target.userPermissions.canSave
  }
  if (target && typeof target.canSave === 'boolean') {
    return target.canSave
  }
  const globalPerms = getGlobalUserPermissions()
  if (globalPerms && typeof globalPerms.canSave === 'boolean') {
    return globalPerms.canSave
  }
  if (target && target.role === 'AUDIT') return false
  if (target && ['ADMIN', 'STAFF'].includes(target.role)) return true

  return true
}




